import { lazy, Suspense, useEffect } from "react";
import {
  IonBadge,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
} from "@ionic/react";
import { Redirect, Route } from "react-router-dom";
import { useResonanceLayout } from "../components/organisms/ResonanceContext/ResonanceLayoutContext";
import LoggedHome from "../pages/LoguedHome/LoguedHome";
import SequenceComponent from "../pages/SequenceComponent/SequenceComponent";
import SequenceComponents from "../pages/SequenceComponents/SequenceComponents";
import FinishedQuestionnaires from "../pages/FinishedQuestionnaires/FinishedQuestionnaires";
import DisplayVideo from "../pages/Video/DisplayVideo";
import DisplayVideos from "../pages/Videos/DisplayVideos";
import DisplayAccount from "../pages/Account/DisplayAccount";
import DisplayResources from "../pages/Resources/DisplayResources";
import DisplayArticle from "../pages/Resources/DisplayArticle";
import ResonanceLoader from "../components/atoms/ResonanceLoader/ResonanceLoader";
import { useAuth } from "../contexts/AuthContext";
import { useConfig } from "../contexts/ConfigContext";
import PrivateRoute from "./PrivateRoute";
import { useStudySlug } from "../hooks/useStudySlug";
import { useResonanceData } from "../components/organisms/ResonanceContext/ResonanceDataContext";
import { useTranslation } from "react-i18next";
import { getDefaultTabPath, getStudyTabs } from "./studyTabs";
import { getCachedInvitationConfigs } from "../services/invitation.service";
import { getCachedStudyLangs } from "../services/study.service";
import SuiviHome from "../pages/Suivi/SuiviHome";
import GroupSpace from "../pages/Suivi/GroupSpace";
import SubjectCalendar from "../pages/Calendar/SubjectCalendar";
import InvitationPage from "../pages/Invitation/InvitationPage";

// Island-map pages lazy-load in ONE shared chunk (see pages/MentalPlusPages/index.ts).
// Their routes stay registered for every study — the chunk only downloads when a
// route is actually visited, so non-island studies never fetch it.
const islandModule = () => import("../pages/MentalPlusPages");
const withSuspense = (
  Component: React.LazyExoticComponent<React.ComponentType<any>>
): React.FC<any> => {
  const Wrapped: React.FC<any> = (props) => (
    <Suspense fallback={<ResonanceLoader />}>
      <Component {...props} />
    </Suspense>
  );
  return Wrapped;
};
const DisplayIsland = withSuspense(
  lazy(() => islandModule().then((m) => ({ default: m.DisplayIsland })))
);
const DisplayHelps = withSuspense(
  lazy(() => islandModule().then((m) => ({ default: m.DisplayHelps })))
);
const DisplayJournal = withSuspense(
  lazy(() => islandModule().then((m) => ({ default: m.DisplayJournal })))
);
const DisplayEvolution = withSuspense(
  lazy(() => islandModule().then((m) => ({ default: m.DisplayEvolution })))
);


const Tabs: React.FC = () => {
  const { disableNav } = useResonanceLayout();
  const { sequences } = useResonanceData();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  // Tabs render under "/:study_slug/tabs", so this is the slug already in the URL
  // (e.g. "mentalplus") — drives the tab links instead of a hardcoded literal.
  const study_slug = useStudySlug();
  // Role-gated tabs (suivi): filter buttons + default tab by the resolved role.
  // selectedRole is committed by AuthContext BEFORE isInitialized flips, so it is
  // stable here; undefined (role missing from welcome) just hides gated tabs.
  const { selectedRole, study } = useConfig();

  // We're now inside the tab outlet, so signup has finished entering the app.
  // Clear the flag here (not in the success page's handleNext) so the state change
  // can't race the landing route into a redirect before navigation settles, and so
  // a later visit to "/" redirects to the app instead of re-showing the signup flow.
  const { signupInProgress, setSignupInProgress, isInitialized, isAuthenticated } =
    useAuth();
  useEffect(() => {
    if (signupInProgress) setSignupInProgress(false);
  }, [signupInProgress, setSignupInProgress]);

  /**
   * Warm the two per-study lookups that decide the SHAPE of the Compte page — its
   * « Langue », « Partager » and « Inviter … » rows. Fetched from the page itself
   * they landed after first paint and inserted rows into a card the user was
   * already reading, so opening Compte visibly jumped. Warming them here, from a
   * component mounted for the whole authenticated session, means the page paints
   * with the rows it will keep.
   *
   * Deliberately fire-and-forget: nothing here blocks the tabs, and both callers
   * still fetch for themselves on a cache miss, so a failure costs the old
   * behaviour rather than a broken page.
   */
  useEffect(() => {
    // Invitation configs are a "users/current" call — gated on a resolved session
    // so a cold entry into /tabs cannot fire it before the token is in place.
    if (!study?.id || !isAuthenticated) return;
    getCachedStudyLangs(study.id).catch(() => {
      /* the Compte page retries on mount */
    });
    getCachedInvitationConfigs(study.id, language).catch(() => {
      /* idem */
    });
  }, [study?.id, language, isAuthenticated]);
  const undoneCount = (sequences ?? [])
    .filter((seq) => seq.status !== "DONE")
    .flatMap((seq) =>
      seq.components.filter((c) => c.questionnaire_id && !c.is_done)
    ).length;

  return (
    <IonTabs
      onIonTabsWillChange={(e) => {
        // broadcast to any listeners (e.g., DisplayVideo instances)
        window.dispatchEvent(
          new CustomEvent("app:tab-will-change", {
            detail: { tab: e.detail.tab },
          })
        );
      }}
    >
      <IonRouterOutlet>
        {/* Bare /tabs lands on the study's landing tab (first of its tab set),
            not a hardcoded "home" — some studies don't have the island-map tab.
            Gated on isInitialized: on a COLD entry straight into /tabs the role
            isn't resolved yet, and redirecting role-blind would send an
            investigator to questionnaires instead of suivi. */}
        <Route
          exact
          path="/:study_slug/tabs"
          render={() =>
            !isInitialized ? null : (
              <Redirect to={getDefaultTabPath(study_slug, selectedRole?.name)} />
            )
          }
        />
        {/* Investigator Suivi dashboard (role-gated tab; the pages self-guard on
            role too, so a deep link by a subject bounces to their landing tab). */}
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(suivi)"
          component={SuiviHome}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(suivi)/groups/:groupKey"
          component={GroupSpace}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(suivi)/groups/:groupKey/sequences/:sequenceId/components/:componentId"
          component={SequenceComponent}
        />
        {/* Subject "Calendrier" (role-gated tab; page self-guards too). */}
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(calendar)"
          component={SubjectCalendar}
        />
        {/* Subject "Inviter" (role-gated tab; a participant invites a relative
            into their own dossier). The page fail-softs when the study has no
            participant-sendable invitation config. */}
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(invitation)"
          component={InvitationPage}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(calendar)/sequences/:sequenceId/components/:componentId"
          component={SequenceComponent}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(home)"
          component={LoggedHome}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(videos)"
          component={DisplayVideos}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(helps)"
          component={DisplayHelps}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(helps)/journal"
          component={DisplayJournal}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(helps)/journal/:noteId"
          component={DisplayJournal}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(evolution)"
          component={DisplayEvolution}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(profile|account)"
          component={DisplayAccount}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(questionnaires)"
          component={SequenceComponents}
        />
        {/* Resources: researcher-managed article library (ARTICLE contents). */}
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(resources)"
          component={DisplayResources}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(resources)/article/:contentId"
          component={DisplayArticle}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(home)/island/:title"
          component={DisplayIsland}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(home)/island/:title/:contentType(Explications|Solutions)"
          component={DisplayVideos}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(home)/island/:title/:contentType(Explications|Solutions)/:id/:type"
          component={DisplayVideo}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(videos)/:id/:type"
          component={DisplayVideo}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(home)/island/:title/sequences/:sequenceId/components/:componentId"
          component={SequenceComponent}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(home)/island/:sequenceId/finished"
          component={FinishedQuestionnaires}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(questionnaires)/sequences/:sequenceId/components/:componentId"
          component={SequenceComponent}
        />
        <PrivateRoute
          exact
          path="/:study_slug/tabs/:tab(home)/island/:title/evolution"
          component={DisplayEvolution}
        />

      </IonRouterOutlet>

      {/* TAB BAR — buttons come from the per-study rules in studyTabs.ts;
          the bar itself is the flat primary dock (common.scss). */}
      {!disableNav && (
        <IonTabBar
          slot="bottom"
          className="floating-tab-bar"
          id="ion-tab-bar"
        >
          {getStudyTabs(study_slug, selectedRole?.name).map((tab) => (
            <IonTabButton
              key={tab.id}
              tab={tab.id}
              href={`/${study_slug}/tabs/${tab.id}`}
            >
              {tab.iconSrc ? (
                <IonIcon src={tab.iconSrc} />
              ) : (
                <IonIcon icon={tab.icon} />
              )}
              <IonLabel>{t(tab.labelKey)}</IonLabel>
              {tab.showUndoneBadge && undoneCount > 0 && (
                <IonBadge color="danger">{undoneCount}</IonBadge>
              )}
            </IonTabButton>
          ))}
        </IonTabBar>
      )}
    </IonTabs>
  );
};

export default Tabs;
